import { useEffect, useMemo, useRef, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { CardSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ImageUpload } from "@/components/image-upload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, QrCode, Landmark, Trash2, CreditCard, User, FileText } from "lucide-react";
import { BankLogo, BANK_BRANDS, detectBankBrand } from "@/components/bank-logo";

export const Route = createFileRoute("/_authenticated/payment-settings")({
  head: () => ({ meta: [{ title: "Pengaturan Pembayaran — MI Al-Munajah Admin" }] }),
  component: PaymentSettingsPage,
});

/** ────────────────────────────────────────────────────────────────
 *  Tipe data terpisah untuk dua konfigurasi independen.
 *  ──────────────────────────────────────────────────────────────── */
type QrisSettings = {
  id: string;
  qris_image_url: string | null;
  qris_merchant_name: string | null;
  updated_at?: string | null;
};

type BankTransferSettings = {
  id: string;
  bank_account_info: string | null;
  updated_at?: string | null;
};

type BankFields = {
  bankName: string;
  accountNumber: string;
  accountName: string;
  notes: string;
};

/** Hapus parameter cache buster lama supaya DB hanya menyimpan URL kanonik. */
function stripCacheBuster(url: string | null): string | null {
  if (!url || !/^https?:\/\//i.test(url)) return url;
  try {
    const u = new URL(url);
    u.searchParams.delete("v");
    u.searchParams.delete("updated_at");
    return u.toString();
  } catch {
    return url;
  }
}

/** Tempel ?v=<updated_at> agar klien (Android/web) selalu mengambil gambar terbaru. */
function withCacheBuster(url: string | null | undefined, version: string | null | undefined): string | null {
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) return url;
  const v = version ? new Date(version).getTime().toString() : Date.now().toString();
  try {
    const u = new URL(url);
    u.searchParams.set("v", v);
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}v=${v}`;
  }
}

function parseBankInfo(text: string | null | undefined): BankFields {
  const result: BankFields = { bankName: "", accountNumber: "", accountName: "", notes: "" };
  if (!text) return result;

  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const remaining: string[] = [];

  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.startsWith("bank:") || lower.startsWith("bank ")) {
      result.bankName = line.replace(/^bank[:\s]*/i, "").trim();
    } else if (lower.includes("no. rek:") || lower.includes("nomor rekening:")) {
      result.accountNumber = line.replace(/.*(?:no\.\s*rek|nomor\s*rekening)[:\s]*/i, "").trim();
    } else if (lower.includes("a.n.") || lower.includes("atas nama:")) {
      result.accountName = line.replace(/.*(?:a\.n\.|atas\s*nama)[:\s.]*/i, "").trim();
    } else {
      remaining.push(line);
    }
  }

  result.notes = remaining.join("\n");
  return result;
}

function buildBankInfo(fields: BankFields) {
  const parts: string[] = [];
  if (fields.bankName.trim()) parts.push(`Bank: ${fields.bankName.trim()}`);
  if (fields.accountNumber.trim()) parts.push(`Nomor Rekening: ${fields.accountNumber.trim()}`);
  if (fields.accountName.trim()) parts.push(`Atas Nama: ${fields.accountName.trim()}`);
  if (fields.notes.trim()) parts.push(fields.notes.trim());
  return parts.join("\n");
}

/** Pastikan ada minimal satu baris di payment_settings dan kembalikan id-nya. */
async function ensurePaymentSettingsId(): Promise<string | null> {
  const { data: rows } = await (supabase.from as any)("payment_settings")
    .select("id").order("id", { ascending: true }).limit(1);
  if (rows && rows.length > 0) return rows[0].id as string;
  const { data: created, error } = await (supabase.from as any)("payment_settings")
    .insert({}).select("id").single();
  if (error) {
    const { data: fallback } = await (supabase.from as any)("payment_settings")
      .select("id").order("id", { ascending: true }).limit(1);
    return fallback && fallback.length > 0 ? (fallback[0].id as string) : null;
  }
  return created?.id ?? null;
}

/** ────────── Fetch terpisah — Android boleh mencontoh pola ini ────────── */

/** Hanya mengambil kolom QRIS. Tidak menyentuh bank_account_info. */
async function fetchQrisSettings(): Promise<QrisSettings | null> {
  const { data, error } = await (supabase.from as any)("payment_settings")
    .select("id, qris_image_url, qris_merchant_name, updated_at")
    .order("id", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data as QrisSettings;
}

/** Hanya mengambil kolom rekening bank. Tidak menyentuh QRIS. */
async function fetchBankTransferSettings(): Promise<BankTransferSettings | null> {
  const { data, error } = await (supabase.from as any)("payment_settings")
    .select("id, bank_account_info, updated_at")
    .order("id", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data as BankTransferSettings;
}

function PaymentSettingsPage() {
  const [qris, setQris] = useCachedState<QrisSettings | null>("payment-settings:qris", null);
  const [bank, setBank] = useCachedState<BankTransferSettings | null>("payment-settings:bank", null);
  const [bankFields, setBankFields] = useState<BankFields>({
    bankName: "", accountNumber: "", accountName: "", notes: "",
  });
  const [savingQris, setSavingQris] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const initRef = useRef(false);

  // Bootstrap: pastikan row ada, lalu muat kedua konfigurasi secara independen.
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    (async () => {
      await ensurePaymentSettingsId();
      const [q, b] = await Promise.all([fetchQrisSettings(), fetchBankTransferSettings()]);
      if (q) setQris(q);
      if (b) {
        setBank(b);
        setBankFields(parseBankInfo(b.bank_account_info));
      }
    })();
  }, []);

  // Realtime: refresh hanya bagian yang berubah, tanpa mencampur dua konfigurasi.
  useEffect(() => {
    const id = qris?.id ?? bank?.id;
    if (!id) return;
    const channel = supabase
      .channel(`payment_settings:${id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "payment_settings", filter: `id=eq.${id}` },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          setQris((prev) => prev ? {
            ...prev,
            qris_image_url: (row.qris_image_url as string | null) ?? null,
            qris_merchant_name: (row.qris_merchant_name as string | null) ?? null,
            updated_at: (row.updated_at as string | null) ?? null,
          } : prev);
          setBank((prev) => {
            const next: BankTransferSettings = {
              id,
              bank_account_info: (row.bank_account_info as string | null) ?? null,
              updated_at: (row.updated_at as string | null) ?? null,
            };
            setBankFields(parseBankInfo(next.bank_account_info));
            return prev ? { ...prev, ...next } : next;
          });
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [qris?.id, bank?.id]);

  const setQ = <K extends keyof QrisSettings>(k: K, v: QrisSettings[K]) =>
    setQris((prev) => prev ? { ...prev, [k]: v } : prev);

  const qrisPreviewUrl = withCacheBuster(qris?.qris_image_url ?? null, qris?.updated_at ?? null);

  /** Hanya menulis kolom QRIS. */
  const saveQrisSettings = async () => {
    if (!qris) return;
    setSavingQris(true);
    const { data, error } = await (supabase.from as any)("payment_settings").update({
      qris_image_url: stripCacheBuster(qris.qris_image_url),
      qris_merchant_name: qris.qris_merchant_name,
      updated_at: new Date().toISOString(),
    }).eq("id", qris.id).select("id, qris_image_url, qris_merchant_name, updated_at").single();

    setSavingQris(false);
    if (error) return toast.error(error.message);
    if (data) setQris(data as QrisSettings);
    toast.success("Pengaturan QRIS disimpan");
  };

  /** Hanya menulis kolom bank_account_info. */
  const saveBankTransferSettings = async () => {
    if (!bank) return;
    setSavingBank(true);
    const built = buildBankInfo(bankFields);
    const { data, error } = await (supabase.from as any)("payment_settings").update({
      bank_account_info: built,
      updated_at: new Date().toISOString(),
    }).eq("id", bank.id).select("id, bank_account_info, updated_at").single();

    setSavingBank(false);
    if (error) return toast.error(error.message);
    if (data) {
      setBank(data as BankTransferSettings);
      setBankFields(parseBankInfo((data as BankTransferSettings).bank_account_info));
    }
    toast.success("Pengaturan rekening bank disimpan");
  };

  const updateBankField = (key: keyof BankFields, value: string) =>
    setBankFields((prev) => ({ ...prev, [key]: value }));

  const bankPreview = useMemo(() => buildBankInfo(bankFields), [bankFields]);

  if (!qris && !bank) return <CardSkeleton lines={6} />;

  return (
    <div>
      <PageHeader
        title="Pengaturan Pembayaran Manual Aplikasi"
        description="Kelola QRIS statis dan informasi rekening transfer secara independen."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ────────── QRIS Section ────────── */}
        <Card className="p-6 rounded-2xl space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <QrCode className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base">Pengaturan QRIS</h3>
              <p className="text-xs text-muted-foreground">
                QRIS statis yang ditampilkan di aplikasi siswa.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-3">
            <div className="w-56 h-56 rounded-2xl border bg-muted/30 flex items-center justify-center overflow-hidden">
              {qrisPreviewUrl
                ? <img src={qrisPreviewUrl} alt="QRIS" className="w-full h-full object-contain" />
                : <QrCode className="w-14 h-14 text-muted-foreground" />}
            </div>
            {qris?.qris_image_url && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setQ("qris_image_url", null)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Hapus QRIS
              </Button>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Upload / Ganti QRIS</Label>
              <ImageUpload
                bucket="school"
                value={qris?.qris_image_url ?? null}
                onChange={(url) => setQ("qris_image_url", url)}
                label={qris?.qris_image_url ? "Ganti QRIS" : "Unggah QRIS"}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nama Merchant QRIS</Label>
              <Input
                value={qris?.qris_merchant_name || ""}
                onChange={(e) => setQ("qris_merchant_name", e.target.value)}
                placeholder="MI AL-MUNAJAH"
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={saveQrisSettings} disabled={savingQris || !qris} className="rounded-xl">
              <Save className="w-4 h-4 mr-2" />
              {savingQris ? "Menyimpan..." : "Simpan QRIS"}
            </Button>
          </div>
        </Card>

        {/* ────────── Bank Transfer Section ────────── */}
        <Card className="p-6 rounded-2xl space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <Landmark className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base">Pengaturan Transfer Bank</h3>
              <p className="text-xs text-muted-foreground">
                Informasi rekening untuk pembayaran manual via transfer.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5 text-muted-foreground" />
                Nama Bank
              </Label>
              <div className="flex items-center gap-3">
                <BankLogo bankName={bankFields.bankName} size="md" />
                <Select
                  value={bankFields.bankName || "__empty__"}
                  onValueChange={(v) => updateBankField("bankName", v === "__empty__" ? "" : v)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Pilih bank" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__empty__">— Pilih bank —</SelectItem>
                    {BANK_BRANDS.map((b) => (
                      <SelectItem key={b.key} value={b.name}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                Nomor Rekening
              </Label>
              <Input
                value={bankFields.accountNumber}
                onChange={(e) => updateBankField("accountNumber", e.target.value)}
                placeholder="1234567890"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
                Atas Nama
              </Label>
              <Input
                value={bankFields.accountName}
                onChange={(e) => updateBankField("accountName", e.target.value)}
                placeholder="MI Al-Munajah"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                Keterangan Tambahan (opsional)
              </Label>
              <Textarea
                rows={3}
                value={bankFields.notes}
                onChange={(e) => updateBankField("notes", e.target.value)}
                placeholder="Cabang, instruksi khusus, dll."
              />
            </div>
          </div>

          <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Preview tampilan di aplikasi siswa
            </p>
            {bankPreview ? (
              <div className="flex items-start gap-3">
                <BankLogo bankName={bankFields.bankName} size="lg" />
                <div className="text-sm whitespace-pre-line leading-relaxed flex-1">
                  {bankPreview}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground italic">Belum ada data rekening.</div>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={saveBankTransferSettings} disabled={savingBank || !bank} className="rounded-xl">
              <Save className="w-4 h-4 mr-2" />
              {savingBank ? "Menyimpan..." : "Simpan Rekening Bank"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
